import { Area } from '../domain/Area'
import { AreaAvailability } from '../domain/AreaAvailability'
import { Availability } from '../domain/Availability'
import { BookingReason } from '../domain/BookingReason'
import { BookingSlot } from '../domain/BookingSlot'
import { BookingSlotOption } from '../domain/BookingSlotOption'
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms'
import { Event } from '../domain/Event'
import { NGBDateTimeAdapter } from '../utilities/NgbDateTimeAdapter'
import { NgbDateNativeAdapter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap/timepicker/ngb-time-struct'

class BookingSlotSelectionOption {

    public date: NgbDateStruct
    public start: NgbTimeStruct
    public end: NgbTimeStruct | null = null
    public reservableArea: Area | null
    public isWaitlistOnly = false
    private waitlistCutOffTime: Date | null = null

    constructor(
        private areaAvailability: AreaAvailability[],
        private ngbDateAdapter: NgbDateNativeAdapter,
        private ngbTimeAdapter: NGBDateTimeAdapter,
        public bookingSlot: BookingSlot,
        public reason: BookingReason | null,
        public event: Event | null,
        public partySize: number,
        private useAnyReasonAvailability: boolean,
        private waitlistsEnabled: boolean
    ) {
        this.date = this.ngbDateAdapter.fromModel(bookingSlot.dateTime)!
        this.start = this.ngbTimeAdapter.fromModel(bookingSlot.dateTime)!
        const firstReservableArea = this.firstReservableArea()
        this.reservableArea = firstReservableArea ? firstReservableArea.area : null
        if (firstReservableArea === null) {
            const waitlistArea = this.waitlistsEnabled ? this.firstWaitlistArea() : null
            this.isWaitlistOnly = waitlistArea !== null
            this.waitlistCutOffTime = waitlistArea?.area.cutOffTime ?? null
            return
        }
        if (this.useAnyReasonAvailability && this.reason === null) {
            return
        }
        const option = this.reservableOption(firstReservableArea)
        if (option === null) {
            return
        }
        const endDateTime = bookingSlot.endDateTime(option.durationMinutes)
        this.end = endDateTime ? this.ngbTimeAdapter.fromModel(endDateTime) : null
    }

    get isReservable() {
        return this.reservableArea !== null
    }

    get isAfterCutOffTime() {
        if (this.reservableArea !== null) {
            return this.bookingSlot.dateTime.getTime() >= this.reservableArea.cutOffTime.getTime()
        }
        if (this.isWaitlistOnly && this.waitlistCutOffTime !== null) {
            return this.bookingSlot.dateTime.getTime() >= this.waitlistCutOffTime.getTime()
        }
        return false
    }

    private firstReservableArea(): AreaAvailability | null {
        return this.areaAvailability
            .find(areaAvailability => this.reservableOption(areaAvailability) !== null) ?? null
    }

    private reservableOption(areaAvailability: AreaAvailability): BookingSlotOption | null {
        if (this.useAnyReasonAvailability && this.reason === null) {
            return areaAvailability.reservableOptionWithAnyReason(
                this.partySize,
                this.event,
                this.bookingSlot.dateTime
            )
        }
        return areaAvailability.reservableOptionWithConfiguration(
            this.partySize,
            this.reason,
            this.event,
            this.bookingSlot.dateTime
        )
    }

    private firstWaitlistArea(): AreaAvailability | null {
        return this.areaAvailability.find(areaAvailability => {
            return areaAvailability.bookingSlots.some(slot => {
                if (slot.bookingSlot.dateTime.getTime() !== this.bookingSlot.dateTime.getTime()) {
                    return false
                }
                return slot.isWaitlistOnlyWithConfiguration(this.partySize, this.reason, this.event)
            })
        }) ?? null
    }
}

@Component({
    selector: 'app-booking-slot-selection',
    templateUrl: './booking-slot-selection.component.html',
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
})
export class BookingSlotSelectionComponent implements OnInit, OnChanges {

    @Input() areaId!: string | null
    @Input() reasonId!: string | null
    @Input() eventId!: string | null
    @Input() partySize!: number
    @Input({ transform: (value: number) => new Date(value) }) idealTime!: Date
    @Input() availability!: Availability
    @Input() useAnyReasonAvailability = false
    @Output() waitlistTimeSelected = new EventEmitter<Date>()
    form!: FormGroup
    options: BookingSlotSelectionOption[] = []
    hasWaitlistOptions = false
    pageConfig = {
        itemsPerPage: 9,
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
    }
    private ngbTimeAdapter = new NGBDateTimeAdapter()
    private ngbDateAdapter = new NgbDateNativeAdapter()

    constructor(
        private rootFormGroup: FormGroupDirective
    ) { }

    ngOnInit() {
        this.form = this.rootFormGroup.form
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['availability'] ||
            changes['areaId'] ||
            changes['reasonId'] ||
            changes['eventId'] ||
            changes['partySize'] ||
            changes['idealTime'] ||
            changes['useAnyReasonAvailability']) {
            this.updateOptions()
        }
    }

    selectOption(option: BookingSlotSelectionOption) {
        if (option.isWaitlistOnly) {
            this.waitlistTimeSelected.emit(option.bookingSlot.dateTime)
            return
        }
        if (option.reservableArea === null) {
            return
        }
        const shouldSetPlaceholderArea = !this.useAnyReasonAvailability ||
            this.areaId != null ||
            !this.availability.venue.customerCanChooseAreaFor(option.reason)
        if (shouldSetPlaceholderArea) {
            this.form.get('selectedAreaId')?.setValue(option.reservableArea.id)
        } else {
            this.form.get('selectedAreaId')?.setValue(null)
        }
        this.form.get('time')?.setValue(option.start)
        if (!this.useAnyReasonAvailability) {
            this.form.get('endTime')?.setValue(option.end)
        }
    }

    private updateOptions() {
        const reason = this.reasonId ? this.availability.venue.reasonWithId(this.reasonId) : null
        const event = this.eventId ? this.availability.venue.eventWithId(this.eventId) : null
        const waitlistsEnabled = this.availability.venue.waitlistsEnabled
        const reservableAreas = this.availability.areaAvailabilitiesOrderedForReason(reason).filter(area => {
            if (this.areaId != null) {
                return area.area.id === this.areaId
            } else if (reason !== null) {
                return area.isConfiguredForReason(reason)
            } else {
                return true
            }
        })
        if (reservableAreas.length === 0) {
            return
        }
        const rangeOfBookingSlots = reservableAreas
            .map(areaAvailability => areaAvailability.bookingSlots)
            .flat()
            .map(bookingSlotAvailability => bookingSlotAvailability.bookingSlot)
            .flat()
            .filter((bookingSlot, index, array) => {
                return array.findIndex(other => other.equals(bookingSlot)) === index
            })
        this.options = rangeOfBookingSlots
            .map(bookingSlot => {
                return new BookingSlotSelectionOption(
                    reservableAreas,
                    this.ngbDateAdapter,
                    this.ngbTimeAdapter,
                    bookingSlot,
                    reason,
                    event,
                    this.partySize,
                    this.useAnyReasonAvailability,
                    waitlistsEnabled
                )
            })
            .filter(option => option.isAfterCutOffTime)
        this.hasWaitlistOptions = this.options.some(option => option.isWaitlistOnly)
        // get index of booking slot closest to ideal time
        const index = this.options
            .map(option => {
                const dateTime = this.ngbTimeAdapter.toModel(option.start)!
                dateTime.setFullYear(this.idealTime.getFullYear(), this.idealTime.getMonth(), this.idealTime.getDate())
                return Math.abs(dateTime.getTime() - this.idealTime.getTime())
            })
            .reduce((prev, curr, index, array) => {
                return curr < array[prev] ? index : prev
            }, 0)
        const perPage = 9
        this.pageConfig.totalPages = Math.ceil(this.options.length / perPage)
        this.pageConfig.currentPage = Math.ceil(index / perPage)
        this.pageConfig.totalItems = this.options.length
    }
}
